/** Иконочная кнопка с активным состоянием, необязательной подписью и подсказкой. */
import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from "react";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { Button } from "./Button";

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
	active?: boolean;
	icon: ReactNode;
	iconSize?: number;
	label?: string;
	/** Класс подписи, если её начертание или цвет отличаются от всей кнопки. */
	labelClassName?: string;
	/** Переносить подпись по словам, если она не помещается в ширину кнопки. */
	wrapLabel?: boolean;
	/** Расположение подписи относительно иконки. */
	labelPosition?: "bottom" | "right";
	tooltip?: {
		className?: string;
		label: string;
		side?: ComponentProps<typeof Tooltip>["side"];
	};
}

export const IconButton = ({
	active,
	icon,
	iconSize = 20,
	label,
	labelClassName = "",
	wrapLabel = false,
	labelPosition = "bottom",
	tooltip,
	className = "",
	type = "button",
	...props
}: IconButtonProps) => {
	const button = (
		<Button
			border={false}
			type={type}
			aria-label={label ?? tooltip?.label}
			aria-pressed={active}
			className={`flex w-full items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
				labelPosition === "right" ? "flex-row gap-2 text-left" : "flex-col gap-1 text-center"
			} ${
				active
					? "bg-accent-soft text-ink hover:bg-accent-soft"
					: "bg-transparent text-ink hover:bg-accent-soft hover:text-ink"
			} ${className}`}
			{...props}
		>
			<Text
				className="flex shrink-0 [&>svg]:size-full"
				style={{ height: iconSize, width: iconSize }}
			>
				{icon}
			</Text>
			{label != null && (
				<Text
					as="span"
					size="small"
					className={`${labelPosition === "right" ? "min-w-0 flex-1 text-left" : "text-center"} leading-tight ${labelClassName}${
						wrapLabel ? " whitespace-normal" : " whitespace-nowrap"
					}`}
				>
					{label}
				</Text>
			)}
		</Button>
	);

	return tooltip ? (
		<Tooltip className={tooltip.className ?? "w-full"} content={tooltip.label} side={tooltip.side}>
			{button}
		</Tooltip>
	) : (
		button
	);
};
